import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Avatar,
  HStack,
  VStack,
  Text,
  Badge,
  useToast,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  email: string;
  displayName: string;
  displayNameOriginal?: string;
  nameChangeCount?: number;
  photoURL: string;
  isAdmin: boolean;
  isPublic: boolean;
  createdAt: any;
  lastLogin: any;
  solveCount: number;
}

export const AdminUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { t } = useTranslation();

  // Edit name modal state
  const { isOpen: isEditNameOpen, onOpen: onEditNameOpen, onClose: onEditNameClose } = useDisclosure();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [resetNameChangeCount, setResetNameChangeCount] = useState(false);
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          solveCount: doc.data().solves?.length || 0
        })) as User[];
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          title: 'Error',
          description: 'Failed to load users',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [toast]);

  const toggleAdmin = async (userId: string, currentValue: boolean) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isAdmin: !currentValue
      });
      setUsers(users.map(user => 
        user.id === userId ? { ...user, isAdmin: !currentValue } : user
      ));
      toast({
        title: 'Success',
        description: `User admin status updated`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const togglePublic = async (userId: string, currentValue: boolean) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isPublic: !currentValue,
        'preferences.isPublic': !currentValue
      });
      setUsers(users.map(user =>
        user.id === userId ? { ...user, isPublic: !currentValue } : user
      ));
      toast({
        title: 'Success',
        description: `User visibility updated`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openEditNameModal = (user: User) => {
    setEditingUser(user);
    setNewDisplayName(user.displayNameOriginal || user.displayName || '');
    setResetNameChangeCount(false);
    onEditNameOpen();
  };

  const handleUpdateDisplayName = async () => {
    if (!editingUser || !newDisplayName.trim()) return;

    setIsUpdatingName(true);
    try {
      const normalizedDisplayName = newDisplayName.toLowerCase().replace(/[^a-z0-9]/g, '');

      const updateData: Record<string, unknown> = {
        displayName: normalizedDisplayName,
        displayNameOriginal: newDisplayName,
        normalizedDisplayName: normalizedDisplayName,
      };

      // Optionally reset the name change count
      if (resetNameChangeCount) {
        updateData.nameChangeCount = 0;
      }

      await updateDoc(doc(db, 'users', editingUser.id), updateData);

      // Update local state
      setUsers(users.map(user =>
        user.id === editingUser.id
          ? {
              ...user,
              displayName: normalizedDisplayName,
              displayNameOriginal: newDisplayName,
              nameChangeCount: resetNameChangeCount ? 0 : user.nameChangeCount
            }
          : user
      ));

      toast({
        title: 'Success',
        description: 'User display name updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onEditNameClose();
    } catch (error) {
      console.error('Error updating display name:', error);
      toast({
        title: 'Error',
        description: 'Failed to update display name',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingName(false);
    }
  };

  return (
    <Card>
      <CardBody>
        <Table>
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Email</Th>
              <Th>Solves</Th>
              <Th>Last Login</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td>
                  <HStack>
                    <Avatar size="sm" name={user.displayNameOriginal || user.displayName} src={user.photoURL} />
                    <VStack align="start" spacing={0}>
                      <HStack>
                        <Text>{user.displayNameOriginal || user.displayName}</Text>
                        <Tooltip label="Edit display name">
                          <IconButton
                            aria-label="Edit name"
                            icon={<EditIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => openEditNameModal(user)}
                          />
                        </Tooltip>
                      </HStack>
                      {user.nameChangeCount !== undefined && user.nameChangeCount > 0 && (
                        <Badge colorScheme="orange" fontSize="xs">
                          {user.nameChangeCount} name change{user.nameChangeCount > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </VStack>
                  </HStack>
                </Td>
                <Td>{user.email}</Td>
                <Td isNumeric>{user.solveCount}</Td>
                <Td>{user.lastLogin?.toDate().toLocaleDateString()}</Td>
                <Td>
                  <HStack>
                    {user.isAdmin && <Badge colorScheme="purple">Admin</Badge>}
                    {user.isPublic ? (
                      <Badge colorScheme="green">Public</Badge>
                    ) : (
                      <Badge colorScheme="red">Private</Badge>
                    )}
                  </HStack>
                </Td>
                <Td>
                  <HStack>
                    <Button
                      size="sm"
                      colorScheme={user.isAdmin ? "red" : "purple"}
                      onClick={() => toggleAdmin(user.id, user.isAdmin)}
                    >
                      {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                    </Button>
                    <Button
                      size="sm"
                      colorScheme={user.isPublic ? "red" : "green"}
                      onClick={() => togglePublic(user.id, user.isPublic)}
                    >
                      {user.isPublic ? 'Make Private' : 'Make Public'}
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardBody>

      {/* Edit Display Name Modal */}
      <Modal isOpen={isEditNameOpen} onClose={onEditNameClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Display Name</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Current Name</FormLabel>
                <Text fontWeight="medium">
                  {editingUser?.displayNameOriginal || editingUser?.displayName}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>New Display Name</FormLabel>
                <Input
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  placeholder="Enter new display name"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  URL will be: /u/{newDisplayName.toLowerCase().replace(/[^a-z0-9]/g, '')}
                </Text>
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={resetNameChangeCount}
                  onChange={(e) => setResetNameChangeCount(e.target.checked)}
                >
                  Reset name change counter (allow user to change again)
                </Checkbox>
                {editingUser?.nameChangeCount !== undefined && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    User has used {editingUser.nameChangeCount} of 2 free changes
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditNameClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateDisplayName}
              isLoading={isUpdatingName}
              isDisabled={!newDisplayName.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}; 